import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const ADMIN_AUTH_KEY = 'unikort_admin_authenticated';

export function AdminLoginPage() {
  const { isAuthenticated, isLoading: authLoading, refreshAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState('');

  // If backend session is already admin, skip to dashboard
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        if (res.data?.isAdmin) {
          sessionStorage.setItem(ADMIN_AUTH_KEY, 'true');
          navigate('/admin/dashboard', { replace: true });
        } else {
          sessionStorage.removeItem(ADMIN_AUTH_KEY);
        }
      } catch {
        sessionStorage.removeItem(ADMIN_AUTH_KEY);
      }
    })();
  }, [navigate]);

  const handleAdminAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError(t('admin.enterCredentials'));
      return;
    }

    setError('');
    setIsAuthenticating(true);

    try {
      const response = await axios.post(
        `${API_URL}/auth/login`,
        { email, password },
        { withCredentials: true }
      );

      if (response.data.success) {
        if (!response.data.user?.isAdmin) {
          setError(t('admin.accessDenied'));
          await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
          return;
        }

        sessionStorage.setItem(ADMIN_AUTH_KEY, 'true');
        // AuthContext fetched /auth/me before this login — refresh it so
        // AdminRoute sees the admin flag instead of bouncing to /login
        await refreshAuth();
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (err: any) {
      // `/auth/login` refuses with a code; the sentence beside it is English and
      // meant for API clients, not for this form (BUG-3).
      const known: Record<string, [string, string]> = {
        invalid_credentials: ['admin.invalidCredentials', 'Неверный e-mail или пароль'],
        use_google_login: ['admin.useGoogleLogin', 'Для этого аккаунта используйте вход через Google'],
        too_many_attempts: [
          'admin.tooManyAttempts',
          'Слишком много попыток входа — попробуйте через 15 минут',
        ],
      };
      // A 429 without a body (a proxy of our own limiter) must still say what
      // happened: «could not sign in» would send someone hunting for a typo
      // that is not there (review r1 #1).
      const code =
        err.response?.data?.code ?? (err.response?.status === 429 ? 'too_many_attempts' : undefined);
      const entry = code ? known[code] : undefined;
      setError(
        entry
          ? t(entry[0], entry[1])
          : t('admin.authFailed', 'Не удалось войти. Попробуйте ещё раз.')
      );
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side */}
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col overflow-x-hidden">
        {/* Logo */}
        <Link to="/" className="w-fit group flex items-center gap-2">
          <img
            src="/logo.png"
            alt="Unikort"
            className="h-9 w-auto"
          />
          <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">
            Unikort
          </span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[420px]">
            <form onSubmit={handleAdminAuth} className="flex flex-col gap-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Lock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500 shrink-0" />
                  <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                    {t('admin.login')}
                  </h1>
                </div>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {t('admin.authenticateAccess')}
                </p>
              </div>

              {error && (
                <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20">
                  {error}
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <Label htmlFor="admin-email">{t('auth.email')}</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@unikort.ee"
                  disabled={isAuthenticating}
                  autoComplete="email"
                />
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <Label htmlFor="admin-password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="pr-12"
                    disabled={isAuthenticating}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={isAuthenticating}>
                {isAuthenticating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('admin.authenticating')}
                  </>
                ) : (
                  t('admin.continueToPortal')
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {t('admin.credentialsHint')}
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Right side: brand panel */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-amber-500 to-orange-600 items-center justify-center">
        <div className="text-center text-white">
          <Lock className="h-24 w-24 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold mb-2">{t('admin.secureAccess')}</h2>
          <p className="text-white/80 max-w-xs">
            {t('admin.secureAccessDesc')}
          </p>
        </div>
      </div>
    </div>
  );
}
